/* Common Imports */
import { Stack } from "@mui/system";

/* MUI Component Imports */
import { CheckCircleOutlined, Close, RocketLaunch } from "@mui/icons-material";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import { ObjectId } from "bson";
import React from "react";
import { setAiBoostFormOfflinePaymentThunk, setAiBoostFormPropertyDetailsThunk } from "../../redux-magic/thunks";
import BeegruButton from "../common-components/buttons/BeegruButton";

/* Icon Imports */

const AiBoostFormOfflineModal = ({
	open,
	handleClose,
	dispatch,
	propertyId,
	source,
}: {
	open: boolean;
	handleClose: () => void;
	dispatch: any;
	propertyId: ObjectId | string;
	source: { url: string; component: string; form_type: string };
}) => {
	/* Component States */

	const [currentStep, setCurrentStep] = React.useState<number>(0);
	const [message, setMessage] = React.useState<string>("");
	const [propertyData, setPropertyData] = React.useState<any>({});
	const [phoneNumber, setPhoneNumber] = React.useState<string>("");
	const [countryCode, setCountryCode] = React.useState<string>("+91");
	const [enterpriseName, setEnterpriseName] = React.useState<string>("");
	const [propertyName, setPropertyName] = React.useState<string>("");
	const [loadingPropertyData, setLoadingPropertyData] = React.useState<boolean>(true);
	const [showCustomSuccess, setShowCustomSuccess] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

	// const [showSuccessSnackbar, setShowSuccessSnackbar] = React.useState(false);

	React.useEffect(() => {
		if (propertyId) {
			fetch(`${process.env.PRODUCTION_API_URL}properties/cards/${propertyId}`, {
				method: "POST",
				headers: {
					"Content-Type": "text/plain",
				},
			})
				.then((res) => res.json())
				.then((data) => {
					setPropertyData(data.property_data);
					setCountryCode(data.property_data.created_by?.business_profile_id.phone.country_code || "");
					setPhoneNumber(data.property_data.created_by?.business_profile_id.phone.phone_number || "");
					setEnterpriseName(data.property_data.created_by?.business_profile_id.title || "");
					setPropertyName(data.property_data.title || "");
					setLoadingPropertyData(false);
					dispatch(
						setAiBoostFormPropertyDetailsThunk({
							property_data: data.property_data,
						}),
					);
				});
		}
	}, [propertyId, dispatch]);

	const handleSubmit = async () => {
		setIsSubmitting(true);
		await dispatch(
			setAiBoostFormOfflinePaymentThunk({
				name: enterpriseName,
				phone: {
					phone_number: phoneNumber,
					country_code: countryCode,
				},
				notes: message,
				source: source,
				properties_to_boost: [
					{
						property_id: propertyData._id,
						property_name: propertyName,
						exists: true,
					},
				],
			}),
		);
		setCurrentStep(1);
		setShowCustomSuccess(true);
		setIsSubmitting(false);
	};

	const resetAndClose = () => {
		setCurrentStep(0);
		setShowCustomSuccess(false);
		setIsSubmitting(false);
		handleClose();
	};
	return (
		<>
			{/* <Snackbar
				open={showSuccessSnackbar}
				autoHideDuration={5000}
				onClose={() => setShowSuccessSnackbar(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setShowSuccessSnackbar(false)}
					severity="success"
					sx={{
						backgroundColor: "#4CAF50",
						color: "white",
						direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						"& .MuiAlert-icon": {
							color: "white",
						},
					}}
				>
					Details Submitted Successfully! Our team will contact you shortly.
				</Alert>
			</Snackbar> */}
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: "1rem",
						maxHeight: "90vh",
					},
				}}
			>
				<DialogTitle sx={{ borderBottom: "2px solid #E0E0E0" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<RocketLaunch sx={{ color: "#693A70" }} />
							<Typography variant="h6">AI Boost</Typography>
						</Box>
						<IconButton onClick={handleClose}>
							<Close />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent>
					{currentStep === 0 ? (
						<Box sx={{ p: 2 }}>
							<Box sx={{ p: 2 }}>
								<Typography
									variant="h6"
									sx={{ mb: 1 }}
								>
									Fill the form
								</Typography>
								<Typography
									variant="body2"
									color="textSecondary"
									sx={{ mb: 3 }}
								>
									Please provide your details so that we can discuss a plan that suits your need
								</Typography>
								<Stack spacing={3}>
									<Box sx={{ display: "flex", gap: 2 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
											>
												Enterprise Name*
											</Typography>
											<TextField
												fullWidth
												value={enterpriseName}
												onChange={(e) => setEnterpriseName(e.target.value)}
												placeholder="Enter your enterprise name"
												autoComplete="off"
											/>
										</Box>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
											<Typography
												variant="body2"
												sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
											>
												Phone Number*
											</Typography>
											<Box sx={{ display: "flex", gap: 1 }}>
												<Select
													value={countryCode}
													onChange={(e) => setCountryCode(e.target.value)}
													sx={{
														minWidth: "100px",
														"& .MuiSelect-select": {
															padding: "0.5rem",
														},
													}}
												>
													<MenuItem value="+91">+91 </MenuItem>
													<MenuItem value="+1">+1</MenuItem>
													<MenuItem value="+971">+971</MenuItem>
												</Select>
												<TextField
													fullWidth
													value={phoneNumber}
													onChange={(e) => {
														const inputValue = e.target.value;
														const numericValue = inputValue.replace(/\D/g, "");

														// Set max length based on country code
														const maxLength =
															countryCode === "+91" ? 10 : countryCode === "+1" ? 10 : countryCode === "+971" ? 9 : 15;

														if (numericValue.length <= maxLength) {
															setPhoneNumber(numericValue);
														}
													}}
													placeholder="Enter your phone number"
													autoComplete="off"
													error={phoneNumber.length > 0 && phoneNumber.length < 10}
													helperText={
														phoneNumber.length > 0 && phoneNumber.length < 10 ? "Phone number must be 10 digits" : ""
													}
													inputProps={{
														inputMode: "numeric",
														pattern: "[0-9]*",
													}}
												/>
											</Box>
										</Box>
									</Box>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
										<Typography
											variant="body2"
											sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
										>
											Property Name
										</Typography>
										<TextField
											fullWidth
											value={propertyName}
											onChange={(e) => setPropertyName(e.target.value)}
											placeholder="Enter property name"
											autoComplete="off"
										/>
									</Box>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
										<Typography
											variant="body2"
											sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
										>
											Notes(optional)
										</Typography>
										<TextField
											fullWidth
											value={message}
											onChange={(e) => setMessage(e.target.value)}
											placeholder="Enter your message"
											multiline
											rows={3}
											autoComplete="off"
										/>
									</Box>
								</Stack>
								<Typography
									variant="body2"
									color="textSecondary"
									sx={{ mt: 2 }}
								>
									Need help?{" "}
									<Typography
										component="span"
										variant="body2"
										sx={{
											color: "#693A70",
											cursor: "pointer",
											fontWeight: 500,
											textDecoration: "none",
											"&:hover": {
												textDecoration: "underline",
											},
										}}
										onClick={() => (window.location.href = "/contact-us")}
									>
										Contact us
									</Typography>
								</Typography>
							</Box>
						</Box>
					) : (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
							}}
						>
							<Box sx={{ p: 4, width: "100%" }}>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										width: "100%",
										textAlign: "center",
										mb: 4,
									}}
								>
									<CheckCircleOutlined
										sx={{
											fontSize: 64,
											color: "#12744F",
											mb: 2,
										}}
									/>
									<Typography
										variant="h6"
										sx={{ mb: 1 }}
									>
										Details Submitted Successfully
									</Typography>
									<Typography
										variant="body1"
										color="textSecondary"
									>
										Our team member will contact you soon, to market you properties.
									</Typography>
								</Box>
								<Box
									sx={{
										width: "100%",
										backgroundColor: "#F0ECF1",
										borderRadius: "1rem",
										p: 3,
										mt: 2,
									}}
								>
									<Typography
										variant="subtitle1"
										sx={{ mb: 0.5, fontWeight: 600 }}
									>
										What&apos;s included ?
									</Typography>
									<Typography
										variant="body1"
										color="textSecondary"
										sx={{ mb: 3 }}
									>
										You will get all these features in action.
									</Typography>

									<Box sx={{ display: "flex", flexDirection: "row", gap: 4, mb: 2 }}>
										<Stack spacing={2}>
											<Box sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1 }}>
												<CheckCircleOutlined sx={{ color: "#1BA672" }} />
												<Typography>Live AI tasks</Typography>
											</Box>
											<Box sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1 }}>
												<CheckCircleOutlined sx={{ color: "#1BA672" }} />
												<Typography>24 x 7 support</Typography>
											</Box>
											<Box sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1 }}>
												<CheckCircleOutlined sx={{ color: "#1BA672" }} />
												<Typography>Premium AI-targeting</Typography>
											</Box>
											<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
												<CheckCircleOutlined sx={{ color: "#1BA672" }} />
												<Typography>Multi-platform marketing</Typography>
												<Box
													sx={{
														backgroundColor: "#FFFFFF",
														padding: 0.75,
														borderRadius: "4px",
														display: "flex",
														alignItems: "center",
													}}
												>
													<img
														src="/images/logo/facebook.png"
														alt="Facebook icon"
														style={{ height: 24 }}
													/>
												</Box>
												<Box
													sx={{
														backgroundColor: "#FFFFFF",
														padding: 0.75,
														borderRadius: "4px",
														display: "flex",
														alignItems: "center",
													}}
												>
													<img
														src="/images/logo/instagram.png"
														alt="Instagram icon"
														style={{ height: 24 }}
													/>
												</Box>
												<Box
													sx={{
														backgroundColor: "#FFFFFF",
														padding: 0.75,
														borderRadius: "4px",
														display: "flex",
														alignItems: "center",
													}}
												>
													<img
														src="/images/logo/google.png"
														alt="GoogleAd icon"
														style={{ height: 24 }}
													/>
												</Box>
											</Box>
										</Stack>
									</Box>
								</Box>
							</Box>
						</Box>
					)}
				</DialogContent>

				<DialogActions
					sx={{
						p: 2,
						borderTop: "2px solid #E0E0E0",
						backgroundColor: "#FFFFFF",
						display: "flex",
						justifyContent: "flex-end",
					}}
				>
					{currentStep === 0 ? (
						<Stack
							direction="row"
							spacing={2}
							sx={{ width: "auto" }}
						>
							<BeegruButton
								variant="text"
								flavor="boost"
								onClick={handleClose}
								sx={{
									flex: 1,
									width: "120px",
									height: "48px",
									color: "#693A70",
									"&:hover": {
										backgroundColor: "transparent",
									},
									"&.MuiButton-text": {
										color: "#693A70",
									},
								}}
							>
								Close
							</BeegruButton>
							<BeegruButton
								variant="contained"
								flavor="boost"
								onClick={handleSubmit}
								disabled={isSubmitting}
								sx={{
									width: "150px",
									height: "48px",
									backgroundColor: "#693A70",
									color: "#FFFFFF",
									"&:hover": {
										backgroundColor: "#532b57",
									},
									"&.Mui-disabled": {
										backgroundColor: "#693A7080",
										color: "#FFFFFF",
									},
								}}
							>
								{isSubmitting ? "Submitting..." : "Submit Details"}
							</BeegruButton>
						</Stack>
					) : (
						<Stack
							direction="row"
							spacing={2}
							sx={{ width: "auto" }}
						>
							<BeegruButton
								flavor="boost"
								onClick={resetAndClose}
								variant="text"
								sx={{
									flex: 1,
									width: "120px",
									height: "48px",
									color: "#693A70",
									"&:hover": {
										backgroundColor: "transparent",
									},
									"&.MuiButton-text": {
										color: "#693A70",
									},
								}}
							>
								Close
							</BeegruButton>
							<BeegruButton
								variant="contained"
								flavor="boost"
								onClick={resetAndClose}
								sx={{
									width: "120px",
									height: "48px",
									backgroundColor: "#693A70",
									color: "#FFFFFF",
									"&:hover": {
										backgroundColor: "#532b57",
									},
								}}
							>
								Done
							</BeegruButton>
						</Stack>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AiBoostFormOfflineModal;
